import Icon from '@components/icons/Icon';
import { StackHeaderProps } from '@react-navigation/stack';
import { Headline2, Headline3, IconButton, theme, Title } from 'aimo-ui';
import useBackButton from 'hooks/useBackButton';
import useBottomMenuHeight from 'hooks/useBottomMenuHeight';
import {
  ExtraPadding,
  SafeAreaHeaderContainerProps,
} from 'navigation/SafeAreaHeaderContainer';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityRole } from 'react-native';
import { usePaymentStore } from 'stores/paymentStore';
import styled from 'styled-components/native';
import { StackHeaderContainer, TitleWrapper } from 'styles/StackHeaderStyles';
import { isWeb } from 'utils/deviceUtils';
import { testIdentifiers } from 'utils/testIdentifiers';
import { useUiStore } from 'stores/uiStore';

type ButtonTypeProps = 'secondary' | 'primary' | 'tertiary';

export type HeaderType = 'default' | 'history' | 'main';
export interface StackHeaderCustomProps
  extends StackHeaderProps,
    SafeAreaHeaderContainerProps {
  background?: string;
  buttonType?: ButtonTypeProps;
  headerBackIconVisible?: boolean;
  titleSize?: 'small' | 'medium' | 'large';
  extraPadding?: ExtraPadding | ExtraPadding[];
  extraPaddingValue?: { [key: string]: number } | number;
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  useInsetForTop?: boolean;
  headerType?: HeaderType;
  stack?: 'park';
}

const FixedWidthWrapper = styled.View`
  flex: 0 0 auto;
`;

const BackIcon: React.FC<{ color?: string }> = ({ color }) => (
  <Icon name="back" size="l" color={color} />
);

const StackHeader = ({
  navigation,
  back,
  background = theme.colors.white,
  options,
  buttonType = 'secondary',
  headerBackIconVisible = true,
  titleSize = 'medium',
  extraPadding = 'all',
  extraPaddingValue,
  leftAlignHeader = true,
  accessibilityLabel,
  accessibilityRole = 'header',
  useInsetForTop,
  stack,
  ...props
}: StackHeaderCustomProps) => {
  const { t } = useTranslation();
  const tabBarHeight = useBottomMenuHeight();
  const {
    headerBackButtonDisplayMode = 'default',
    headerMode = 'screen',
    headerShown = true,
    headerRight,
    headerTitle,
  } = options || {};
  const HeaderRight = headerRight as React.FC;
  const HeaderTitle = headerTitle as React.FC;
  const title = options?.title || '';
  const iconColor =
    buttonType === 'secondary' ? theme.colors.white : theme.colors.black;

  const externalFlowComplete = usePaymentStore(
    (s) => s.externalPaymentFlowCompleted
  );
  const externalLoading = usePaymentStore((s) => s.externalPaymentFlowLoading);

  const externalFlowStuck = !externalFlowComplete && externalLoading;

  const backHandler = () => {
    if (externalFlowStuck) {
      useUiStore.getState().setSheetModal('externalPaymentStuck');
    } else {
      navigation.goBack();
    }
  };

  const handler = () => {
    backHandler();
    return true;
  };
  useBackButton(handler);

  const handleNavigation = () => {
    if (back) {
      if (navigation.canGoBack()) {
        backHandler();
      } else {
        navigation.navigate('mainTabs', {
          screen: 'accountScreen',
          params: { screen: 'accountMain' },
        });
      }
    } else if (stack === 'park') {
      navigation.popTo('park');
    } else {
      navigation.navigate('mainTabs', {
        screen: 'accountScreen',
        params: { screen: 'accountMain' },
      });
    }
  };

  let CustomTitle;

  switch (titleSize) {
    case 'small':
      CustomTitle = Headline3;
      break;
    case 'large':
      CustomTitle = Title;
      break;
    case 'medium':
    default:
      CustomTitle = Headline2;
      break;
  }

  const BackComponent = useCallback(
    () => <BackIcon color={iconColor} />,
    [iconColor]
  );

  if (!headerShown) {
    return <></>;
  }

  // Generate a default accessibility label if none is provided
  const generatedAccessibilityLabel =
    accessibilityLabel ??
    (title ? `${t(title)} ${t('ariaLabels.common.section')}` : '');

  return (
    <StackHeaderContainer
      {...props}
      tabBarHeight={tabBarHeight}
      background={background}
      headerMode={headerMode}
      extraPadding={extraPadding}
      extraPaddingValue={extraPaddingValue}
      leftAlignHeader={leftAlignHeader}
      accessible={false}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={generatedAccessibilityLabel}
      importantForAccessibility="yes"
      accessibilityElementsHidden={false}
      useInsetForTop={useInsetForTop}
      headline={
        headerBackButtonDisplayMode !== 'minimal' && (
          <TitleWrapper mr={headerBackIconVisible && isWeb}>
            {HeaderTitle ? (
              <HeaderTitle />
            ) : (
              <CustomTitle
                isEllip={true}
                center={isWeb}
                accessible={true}
                accessibilityRole="header"
                accessibilityLabel={generatedAccessibilityLabel}
                importantForAccessibility="yes"
                {...testIdentifiers('stack-header-title')}>
                {title}
              </CustomTitle>
            )}
          </TitleWrapper>
        )
      }
      leftContent={
        headerBackIconVisible && (
          <IconButton
            rounded
            padding={8}
            buttonType={buttonType}
            fullWidth={false}
            onPress={handleNavigation}
            {...testIdentifiers('stack-header-back')}
            iconComponent={BackComponent}
            accessibilityLabel={t('ariaLabels.goBack')}
            accessibilityRole="button"
            accessibilityHint={t('ariaLabels.goBackHint')}
          />
        )
      }
      rightContent={
        !!HeaderRight && (
          <FixedWidthWrapper>
            <HeaderRight />
          </FixedWidthWrapper>
        )
      }
    />
  );
};

export default StackHeader;
